import { useState, useEffect, useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  getAdminDashboard,
  getAdminCommission,
  getCommissionPayments,
  getCommissionSummary,
  toggleCommissionPayment,
  clearToggleError,
} from "../store/slices/adminReportSlice";
import { showApiError, showApiSuccess } from "../utils/toast";

// ─── constants ────────────────────────────────────────────────────────────────

export const TABS = { OVERVIEW: "overview", SETTLEMENTS: "settlements" };

export const PERIODS = [
  { value: "today", label: "Today" },
  { value: "week",  label: "This Week" },
  { value: "month", label: "This Month" },
  { value: "year",  label: "This Year" },
];

export const STATUS_OPTIONS = [
  { value: "all",    label: "All" },
  { value: "paid",   label: "Paid" },
  { value: "unpaid", label: "Unpaid" },
];

export const MONTHS = [
  { value: 1,  label: "January"   },
  { value: 2,  label: "February"  },
  { value: 3,  label: "March"     },
  { value: 4,  label: "April"     },
  { value: 5,  label: "May"       },
  { value: 6,  label: "June"      },
  { value: 7,  label: "July"      },
  { value: 8,  label: "August"    },
  { value: 9,  label: "September" },
  { value: 10, label: "October"   },
  { value: 11, label: "November"  },
  { value: 12, label: "December"  },
];

const now = new Date();
const currentYear  = now.getFullYear();
const currentMonth = now.getMonth() + 1;
// Default settlements to previous month (backend pattern)
const defaultMonth = currentMonth === 1 ? 12 : currentMonth - 1;
const defaultYear  = currentMonth === 1 ? currentYear - 1 : currentYear;

export const YEARS = Array.from(
  { length: currentYear - 2023 },
  (_, i) => currentYear - i
);

// ─── hook ─────────────────────────────────────────────────────────────────────

export const useAdminReport = () => {
  const dispatch = useDispatch();
  const {
    dashboardData, dashboardLoading, dashboardError,
    commissionData, commissionLoading, commissionError,
    payments, paymentsPagination, paymentsFilter, paymentsLoading, paymentsError,
    summary, summaryLoading, summaryError,
    toggleLoadingMap, toggleError,
  } = useSelector((state) => state.adminReport);

  // ── tab state ──────────────────────────────────────────────────────────────
  const [activeTab, setActiveTab] = useState(TABS.OVERVIEW);

  // ── overview filters ───────────────────────────────────────────────────────
  const [period, setPeriod] = useState("month");

  // ── settlements filters ────────────────────────────────────────────────────
  const [settlementFilters, setSettlementFilters] = useState({
    month:    defaultMonth,
    year:     defaultYear,
    status:   "all",
    search:   "",
    page:     1,
    per_page: 15,
  });

  // Debounce search so we don't fire on every keystroke
  const searchTimerRef = useRef(null);

  // ── fetch overview ─────────────────────────────────────────────────────────
  const fetchOverview = useCallback(async (p = period) => {
    await Promise.all([
      dispatch(getAdminDashboard(p)),
      dispatch(getAdminCommission(p)),
    ]);
  }, [dispatch, period]);

  // ── fetch settlements ──────────────────────────────────────────────────────
  const fetchSettlements = useCallback(async (filters = settlementFilters) => {
    await Promise.all([
      dispatch(getCommissionPayments(filters)),
      dispatch(getCommissionSummary({ month: filters.month, year: filters.year })),
    ]);
  }, [dispatch, settlementFilters]);

  // ── auto-fetch on tab / filter changes ────────────────────────────────────
  useEffect(() => {
    if (activeTab === TABS.OVERVIEW) {
      fetchOverview(period);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, period]);

  useEffect(() => {
    if (activeTab === TABS.SETTLEMENTS) {
      fetchSettlements(settlementFilters);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    activeTab,
    settlementFilters.month,
    settlementFilters.year,
    settlementFilters.status,
    settlementFilters.page,
    settlementFilters.per_page,
  ]);

  // Debounced search trigger
  useEffect(() => {
    if (activeTab !== TABS.SETTLEMENTS) return;
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      fetchSettlements({ ...settlementFilters, page: 1 });
    }, 400);
    return () => clearTimeout(searchTimerRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settlementFilters.search]);

  // ── actions ────────────────────────────────────────────────────────────────
  const handleTabChange = useCallback((tab) => {
    setActiveTab(tab);
  }, []);

  const handlePeriodChange = useCallback((p) => {
    setPeriod(p);
  }, []);

  const updateSettlementFilter = useCallback((key, value) => {
    setSettlementFilters((prev) => ({
      ...prev,
      [key]: value,
      // reset page on any filter change except page itself
      ...(key !== "page" ? { page: 1 } : {}),
    }));
  }, []);

  const handleSettlementPageChange = useCallback((page) => {
    setSettlementFilters((prev) => ({ ...prev, page }));
  }, []);

  const handleTogglePayment = useCallback(async (uuid, currentIsPaid) => {
    const newIsPaid = !currentIsPaid;
    try {
      const result = await dispatch(toggleCommissionPayment({ uuid, is_paid: newIsPaid }));
      if (toggleCommissionPayment.fulfilled.match(result)) {
        showApiSuccess(newIsPaid ? "Marked as paid" : "Marked as unpaid");
        // Refresh summary totals to reflect the change
        dispatch(getCommissionSummary({
          month: settlementFilters.month,
          year:  settlementFilters.year,
        }));
      } else {
        showApiError(null, result.payload || "Failed to update status");
        dispatch(clearToggleError());
      }
    } catch {
      showApiError(null, "Failed to update status");
    }
  }, [dispatch, settlementFilters.month, settlementFilters.year]);

  const refreshOverview  = useCallback(() => fetchOverview(period), [fetchOverview, period]);
  const refreshSettlements = useCallback(() => fetchSettlements(settlementFilters), [fetchSettlements, settlementFilters]);

  // ── derived helpers ────────────────────────────────────────────────────────
  const selectedMonthLabel = MONTHS.find((m) => m.value === settlementFilters.month)?.label || "";
  const overviewLoading    = dashboardLoading || commissionLoading;
  const overviewError      = dashboardError   || commissionError;

  return {
    // Tab
    activeTab,
    handleTabChange,

    // Overview
    period,
    handlePeriodChange,
    dashboardData,
    commissionData,
    overviewLoading,
    overviewError,
    refreshOverview,

    // Settlements
    settlementFilters,
    updateSettlementFilter,
    handleSettlementPageChange,
    payments,
    paymentsPagination,
    paymentsFilter,
    paymentsLoading,
    paymentsError,
    summary,
    summaryLoading,
    refreshSettlements,

    // Toggle
    toggleLoadingMap,
    handleTogglePayment,

    // Constants
    PERIODS,
    STATUS_OPTIONS,
    MONTHS,
    YEARS,
    selectedMonthLabel,
    TABS,
  };
};

export default useAdminReport;
